// Client-side post-quantum crypto (NIST FIPS 204 · ML-DSA-65).
// The private key never leaves the browser: we sign the server's login
// challenge locally and only send the signature. Interop with the backend's
// Python `pqcrypto.sign.ml_dsa_65` is verified bidirectionally.
import { ml_dsa65 } from "@noble/post-quantum/ml-dsa.js";
import { x25519 } from "@noble/curves/ed25519.js";

export const hexToBytes = (hex) => {
  const clean = (hex || "").trim();
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
};

export const bytesToHex = (bytes) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

// Sign the challenge nonce with the ML-DSA-65 secret key. Returns hex signature.
export const signChallenge = (nonceHex, secretKeyHex) => {
  const nonce = hexToBytes(nonceHex);
  const sk = hexToBytes(secretKeyHex);
  const sig = ml_dsa65.sign(nonce, sk);
  return bytesToHex(sig);
};

// Normalise a keyfile JSON to { lns, sk, pk }. Supports the real backend
// schema (lns_name / secret_key_hex / public_key_hex).
export const parseKeyfile = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  const lns = obj.lns_name || obj.lns || null;
  const sk = obj.secret_key_hex || obj.private_key || null;
  const pk = obj.public_key_hex || obj.public_key || null;
  if (!lns || !sk) return null;
  return { lns, sk, pk, tenant_id: obj.tenant_id || null, did: obj.did || null };
};

// ── Pulse E2EE (NIST FIPS 203 · ML-KEM-768 + AES-256-GCM) ──
// The ML-KEM keypair is DERIVED deterministically from the user's ML-DSA
// secret key, so it is reproducible on any device that holds the keyfile —
// no extra key file to manage. Plaintext never leaves the browser.
import { ml_kem768 } from "@noble/post-quantum/ml-kem.js";

const _enc = new TextEncoder();
const _dec = new TextDecoder();

export async function deriveKemKeypair(skHex) {
  const material = _enc.encode("lattice-pulse-mlkem-v1:" + (skHex || ""));
  const seedBuf = await crypto.subtle.digest("SHA-512", material); // 64 bytes
  return ml_kem768.keygen(new Uint8Array(seedBuf));
}

const _aesKey = (secret) => crypto.subtle.importKey("raw", secret, "AES-GCM", false, ["encrypt", "decrypt"]);

// recipientPubHex: { lns: kem_pk_hex }. Returns [{ for, kem, iv, ct }] envelopes.
export async function encryptForRecipients(plaintext, recipientPubHex) {
  const pt = _enc.encode(plaintext);
  const envelopes = [];
  for (const [lns, pkHex] of Object.entries(recipientPubHex)) {
    if (!pkHex) continue;
    const { cipherText, sharedSecret } = ml_kem768.encapsulate(hexToBytes(pkHex));
    const key = await _aesKey(sharedSecret);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, pt));
    envelopes.push({ for: lns, kem: bytesToHex(cipherText), iv: bytesToHex(iv), ct: bytesToHex(ct) });
  }
  return envelopes;
}

export async function decryptEnvelope(env, secretKey) {
  try {
    const sharedSecret = ml_kem768.decapsulate(hexToBytes(env.kem), secretKey);
    const key = await _aesKey(sharedSecret);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: hexToBytes(env.iv) }, key, hexToBytes(env.ct));
    return _dec.decode(pt);
  } catch { return null; }
}

// ── E2EE attachments (AES-256-GCM, key shared via the ML-KEM envelope) ──
// Files are encrypted locally; the server only ever stores opaque ciphertext.
export async function encryptFile(file) {
  const buf = new Uint8Array(await file.arrayBuffer());
  const keyRaw = crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey("raw", keyRaw, "AES-GCM", false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, buf));
  return {
    ciphertext: ct,
    key: bytesToHex(keyRaw),
    iv: bytesToHex(iv),
    name: file.name,
    mime: file.type || "application/octet-stream",
    size: file.size,
  };
}

export async function decryptFileBytes(ciphertext, keyHex, ivHex) {
  const key = await crypto.subtle.importKey("raw", hexToBytes(keyHex), "AES-GCM", false, ["decrypt"]);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: hexToBytes(ivHex) }, key, ciphertext);
  return new Uint8Array(pt);
}

// ── Allegati grandi: pezzi da 4 MB, un nonce per pezzo ──
// Un file da 100 MB non si può cifrare in un colpo solo su un telefono, e nemmeno su un
// portatile modesto: si lavora un pezzo alla volta. La chiave resta una sola; il nonce
// cambia per pezzo (gli ultimi quattro byte sono il numero del pezzo), come prescrive
// AES-GCM. Il manifest porta `cs` (dimensione del pezzo) e `n` (quanti pezzi): senza quei
// campi l'allegato è del formato precedente e si apre come prima.
export const ATT_CHUNK = 4 * 1024 * 1024;
export const ATT_TAG = 16;

export function attNonce(iv, index) {
  const n = new Uint8Array(12);
  n.set(iv.subarray(0, 8));
  n[8] = (index >>> 24) & 255;
  n[9] = (index >>> 16) & 255;
  n[10] = (index >>> 8) & 255;
  n[11] = index & 255;
  return n;
}

export const isChunkedAtt = (att) => !!(att && att.n && att.cs);

export const importAesKey = (keyHex, usage) =>
  crypto.subtle.importKey("raw", hexToBytes(keyHex), "AES-GCM", false, usage);

export async function encryptAttChunk(key, iv, index, bytes) {
  return new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: attNonce(iv, index) }, key, bytes));
}

// Decifra un allegato e restituisce i pezzi in chiaro (uno solo per il formato vecchio).
// I pezzi restano separati di proposito: si passano a un Blob senza ricopiare il file.
export async function decryptAttachment(ciphertext, att) {
  if (!isChunkedAtt(att)) return [await decryptFileBytes(ciphertext, att.key, att.iv)];
  const key = await importAesKey(att.key, ["decrypt"]);
  const iv = hexToBytes(att.iv);
  const step = att.cs + ATT_TAG;
  const parts = [];
  for (let i = 0; i < att.n; i++) {
    const start = i * step;
    if (start >= ciphertext.length) break;
    const slice = ciphertext.subarray(start, Math.min(ciphertext.length, start + step));
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: attNonce(iv, i) }, key, slice);
    parts.push(new Uint8Array(pt));
  }
  return parts;
}

// Pulse message payload: structured JSON ({ text, attachments[] }) with a version
// marker so plain-text (legacy/federated) messages still decode cleanly.
// `r` (citazione della risposta) e `ttl` sono gli stessi campi dell'app mobile: un
// messaggio scritto dal browser si apre sul telefono con la sua citazione, e viceversa.
// I client vecchi ignorano i campi che non conoscono, quindi si aggiunge senza rompere.
export function packMessage(text, attachments, reply, ttl, forwarded) {
  return JSON.stringify({ __lat: 1, t: text || "", a: attachments || [], r: reply || null, ttl: ttl || 0, f: forwarded ? 1 : 0 });
}
export function unpackMessage(plaintext) {
  if (typeof plaintext !== "string") return { text: "", atts: [], reply: null, ttl: 0 };
  try {
    const o = JSON.parse(plaintext);
    if (o && typeof o === "object" && o.__lat === 1) {
      return { text: o.t || "", atts: Array.isArray(o.a) ? o.a : [], reply: o.r || null, ttl: o.ttl || 0, forwarded: !!o.f };
    }
  } catch { /* legacy plain text */ }
  return { text: plaintext, atts: [], reply: null, ttl: 0 };
}

// ── Gruppi E2EE (chiave di gruppo AES-256 + involucro ML-KEM-768) ──
// Con 10000 membri una busta per destinatario sarebbe impensabile: si cifra UNA volta con
// la chiave di gruppo, e quella chiave viaggia dentro un involucro ML-KEM per ciascun
// membro. Formato identico a `src/lib/crypto.js` dell'app mobile (interoperabile).
export function generateGroupKey() {
  return crypto.getRandomValues(new Uint8Array(32));
}

export async function wrapGroupKeyFor(groupKey, recipientPubHex) {
  const { cipherText, sharedSecret } = ml_kem768.encapsulate(hexToBytes(recipientPubHex));
  const key = await _aesKey(sharedSecret);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, groupKey));
  return { kem: bytesToHex(cipherText), iv: bytesToHex(iv), ct: bytesToHex(ct) };
}

export async function unwrapGroupKey(env, kemSecretKey) {
  const sharedSecret = ml_kem768.decapsulate(hexToBytes(env.kem), kemSecretKey);
  const key = await _aesKey(sharedSecret);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: hexToBytes(env.iv) }, key, hexToBytes(env.ct));
  return new Uint8Array(pt);
}

export async function encryptWithGroupKey(plaintext, groupKey) {
  const key = await _aesKey(groupKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, _enc.encode(plaintext)));
  return { iv: bytesToHex(iv), ct: bytesToHex(ct) };
}

export async function decryptWithGroupKey(ivHex, ctHex, groupKey) {
  try {
    const key = await _aesKey(groupKey);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: hexToBytes(ivHex) }, key, hexToBytes(ctHex));
    return _dec.decode(pt);
  } catch { return null; }
}

// ── INVITI CON LINK ───────────────────────────────────────────────────────────
// Il segreto dell'invito non è una password: è una chiave, e va trattata come tale.
// Vive nella parte del link DOPO IL CANCELLETTO, che il browser non manda al server, non
// scrive nei log e non mette nemmeno nell'header Referer.
//
// Due derivazioni SEPARATE dallo stesso segreto, con etichette diverse:
//  · `lattice-invite-key-v1:`  → la chiave AES che sigilla la chiave di gruppo
//  · `lattice-invite-hash-v1:` → l'impronta che il server usa per riconoscere il link
// Separarle è il punto: il server conosce l'impronta, e da quella non si torna alla
// chiave. Con una sola derivazione, chi legge il database avrebbe in mano tutto.
const _INV_KEY = "lattice-invite-key-v1:";
const _INV_HASH = "lattice-invite-hash-v1:";

async function _sha256(str) {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", _enc.encode(str)));
}

/// 32 byte casuali in base64url: nessun carattere da sfuggire nell'indirizzo.
export function newInviteSecret() {
  const b = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function inviteTokenHash(secret) {
  return bytesToHex(await _sha256(_INV_HASH + secret));
}

export async function sealForInvite(groupKey, secret) {
  const key = await _aesKey(await _sha256(_INV_KEY + secret));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, groupKey));
  return { iv: bytesToHex(iv), ct: bytesToHex(ct) };
}

export async function openInvite(blob, secret) {
  const key = await _aesKey(await _sha256(_INV_KEY + secret));
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: hexToBytes(blob.iv) }, key, hexToBytes(blob.ct));
  return new Uint8Array(pt);
}

// ── NUOVA IDENTITÀ (registrazione dal browser) ────────────────────────────────
// Le stesse tre funzioni che l'app usa in `SignupScreen`. La chiave privata nasce qui e
// non esce: al server va solo la parte pubblica e una firma che dimostra che la privata
// esiste. Se questo file girasse sul server, la promessa di tutto il progetto sarebbe
// falsa — ed è per questo che la registrazione si fa nel browser e non con una chiamata.
export function keygen() {
  const kp = ml_dsa65.keygen(crypto.getRandomValues(new Uint8Array(32)));
  return { publicKeyHex: bytesToHex(kp.publicKey), secretKeyHex: bytesToHex(kp.secretKey) };
}

/// Firma di un messaggio qualsiasi (non il nonce di accesso): serve al server per
/// verificare che chi registra un nome possieda davvero la chiave che sta dichiarando.
export function signMessage(message, secretKeyHex) {
  if (!secretKeyHex) return "";
  return bytesToHex(ml_dsa65.sign(_enc.encode(String(message)), hexToBytes(secretKeyHex)));
}

/// Chiave Diffie-Hellman della cassetta anonima, DERIVATA dalla chiave privata: identica
/// a quella che ricava il telefono dallo stesso file chiave (`lattice-anon-dh-v1:`), o
/// browser e telefono non si troverebbero sullo stesso segreto condiviso.
export async function deriveDhKeypair(skHex) {
  const material = _enc.encode("lattice-anon-dh-v1:" + (skHex || ""));
  const full = new Uint8Array(await crypto.subtle.digest("SHA-512", material));
  const seed = full.slice(0, 32);
  return { secretKey: seed, publicKey: x25519.getPublicKey(seed) };
}
